import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { groupsData } from 'src/data/mockData';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from 'src/components/ui/table';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious,
} from 'src/components/ui/pagination';

type SortField = 'id' | 'label' | 'representative' | 'city' | 'email' | 'policyHolders' | 'created' | 'agent';
type SortDirection = 'asc' | 'desc';

const navigationItems = [
  { icon: 'tree-icn.svg', label: 'Tree' },
  { icon: 'map.svg', label: 'Map' },
  { icon: 'print.svg', label: 'Print' },
  { icon: 'mail.svg', label: 'Email' },
  { icon: 'text.svg', label: 'Text' },
  { icon: 'invoice.svg', label: 'Invoice' },
  { icon: 'update.svg', label: 'Update' },
  { icon: 'work.svg', label: 'Work' },
  { icon: 'export.svg', label: 'Export' },
];

const RegularGroups = () => {
  const navigate = useNavigate();
  const params = useParams();
  // States for the table interface
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [selectedGroup, setSelectedGroup] = useState<typeof groupsData[0] | null>(null);
  const [activeTab, setActiveTab] = useState('information');
  const [groups, setGroups] = useState(groupsData);
  const [isEditingAgentGroup, setIsEditingAgentGroup] = useState(false);
  const [isEditingSetup, setIsEditingSetup] = useState(false);
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [editData, setEditData] = useState({
    company: '',
    representative: '',
    agentGroupId: '',
    createdOn: '',
    address: '45 W 139th St, New York, New York(NY), 10037',
    localTime: '08:30 am',
    parent: '',
    website: '',
    management: '',
    domainName: '',
    secureDomainName: '',
    status: 'Active',
    taxId: '••••••45643',
    listBillInvoice: 'Yes - Online ACH - Summary',
    invoiceTemplate: 'Default Invoice',
  });
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    groupId: string;
    x: number;
    y: number;
  }>({
    isOpen: false,
    groupId: '',
    x: 0,
    y: 0,
  });
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    new Set(['1'])
  );
  const itemsPerPage = 12;

  // Check URL params and open detail view if groupId is present
  useEffect(() => {
    if (params.groupId && !selectedGroup) {
      const group = groupsData.find(g => g.id === params.groupId);
      if (group) {
        setSelectedGroup(group);
        setViewMode('detail');
      }
    }
  }, [params.groupId, selectedGroup]);

  // Update editData when selectedGroup changes
  useEffect(() => {
    if (selectedGroup) {
      setEditData({
        company: selectedGroup.label || '',
        representative: selectedGroup.representative || '',
        agentGroupId: selectedGroup.id || '',
        createdOn: selectedGroup.created || '',
        address: '45 W 139th St, New York, New York(NY), 10037',
        localTime: '08:30 am',
        parent: selectedGroup.agent || '',
        website: `www.${selectedGroup.label?.toLowerCase().replace(/\s+/g, '') || ''}.com`,
        management: `www.${selectedGroup.label?.toLowerCase().replace(/\s+/g, '') || ''}.com/manage`,
        domainName: `${selectedGroup.label?.toLowerCase().replace(/\s+/g, '') || ''}.com`,
        secureDomainName: `www.${selectedGroup.label?.toLowerCase().replace(/\s+/g, '') || ''}.com`,
        status: 'Active',
        taxId: '••••••45643',
        listBillInvoice: 'Yes - Online ACH - Summary',
        invoiceTemplate: 'Default Invoice',
      });
    }
  }, [selectedGroup]);

  const filteredData = groups.filter(group => 
    group.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.representative.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedData = [...filteredData].sort((a, b) => {
    let aValue: string | number = a[sortField];
    let bValue: string | number = b[sortField];

    if (sortField === 'created') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    } else if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <img src="/icons/sort-arrow.svg" alt="sort" className="w-4 h-4" />;
    }
    return (
      <img
        src="/icons/sort-arrow.svg"
        alt="sort"
        className={`w-4 h-4 ${sortDirection === 'asc' ? 'rotate-180' : ''}`}
      />
    );
  };

  const handleToggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleCloseMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleContextMenuClick = (e: React.MouseEvent, groupId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      isOpen: true,
      groupId,
      x: rect.left - 100,
      y: rect.bottom + 10,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({
      isOpen: false,
      groupId: '',
      x: 0,
      y: 0,
    });
  };

  const handleViewGroup = (groupId: string) => {
    const group = groupsData.find(g => g.id === groupId);
    if (group) {
      setSelectedGroup(group);
      setViewMode('detail');
      handleCloseContextMenu();
      // Navigate to detail view with group ID in URL
      navigate(`/groups/${groupId}`);
    }
  };

  const handleDeleteGroup = (groupId: string) => {
    setGroups(prevGroups => prevGroups.filter(group => group.id !== groupId));
    handleCloseContextMenu();
  };


  const handleWebsiteGroup = (groupId: string) => {
    console.log('Open website for group:', groupId);
    handleCloseContextMenu();
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedGroup(null);
    // Use browser back button instead of just changing state
    navigate(-1);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleSaveAgentGroup = () => {
    setIsEditingAgentGroup(false);
  };

  const handleCancelAgentGroup = () => {
    setEditData(prev => ({
      ...prev,
      company: selectedGroup?.label || '',
      representative: selectedGroup?.representative || '',
      agentGroupId: selectedGroup?.id || '',
      createdOn: selectedGroup?.created || '',
      address: '45 W 139th St, New York, New York(NY), 10037',
      localTime: '08:30 am',
    }));
    setIsEditingAgentGroup(false);
  };

  const handleSaveSetup = () => {
    setIsEditingSetup(false);
  };

  const handleCancelSetup = () => {
    setEditData(prev => ({
      ...prev,
      parent: selectedGroup?.agent || '',
      website: `www.${selectedGroup?.label?.toLowerCase().replace(/\s+/g, '') || ''}.com`,
      management: `www.${selectedGroup?.label?.toLowerCase().replace(/\s+/g, '') || ''}.com/manage`,
      domainName: `${selectedGroup?.label?.toLowerCase().replace(/\s+/g, '') || ''}.com`,
      secureDomainName: `www.${selectedGroup?.label?.toLowerCase().replace(/\s+/g, '') || ''}.com`,
      status: 'Active',
      taxId: '••••••45643',
    }));
    setIsEditingSetup(false);
  };

  const handleSaveGroup = () => {
    setIsEditingGroup(false);
  };

  const handleCancelGroup = () => {
    setEditData(prev => ({
      ...prev,
      listBillInvoice: 'Yes - Online ACH - Summary',
      invoiceTemplate: 'Default Invoice',
    }));
    setIsEditingGroup(false);
  };

  const toggleProductExpanded = (productId: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const renderProductsTab = () => (
    <div className="mt-6">
      {/* Search and Add Product Section */}
      <div className="flex justify-between items-center mb-6 max-md:flex-col max-md:gap-4">
        <div className="flex gap-4 w-[60%] max-md:w-full">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search by title or ID..."
              className="w-full px-4 py-3 pl-8 h-10 border rounded-lg focus:outline-none focus:ring-2 id-card-input"
            />
            <img
              src="/icons/Search.svg"
              alt="search"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
            />
          </div>
          <button className="px-6 py-2 rounded-full bg-secondary-bg text-white hover:bg-opacity-90 transition-colors max-sm:hidden">
            Search
          </button>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-white rounded-full hover:bg-gray-50 transition-colors max-md:hidden">
          <img src="/icons/plus.svg" alt="add" className="w-4 h-4" />
          Add Product
        </button>
      </div>

      {/* Product Cards */}
      <div className="space-y-4">
        {/* Processing Fee Product */}
        <div className="bg-white rounded-xl overflow-hidden">
          <div className="p-4 pt-6 pl-6 max-md:p-3">
            <div className="flex justify-between items-start max-lg:items-center">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  Processing Fee
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button className="hidden md:block p-3 hover:bg-black/10 rounded-full bg-black/5 transition-colors">
                  <img
                    src="/icons/members/edit.svg"
                    alt="edit"
                    className="w-4 h-4"
                  />
                </button>
                <button className="hidden md:block p-3 hover:bg-black/10 rounded-full bg-black/5 transition-colors">
                  <img
                    src="/icons/delete.svg"
                    alt="delete"
                    className="w-4 h-4"
                  />
                </button>
                <button 
                  onClick={() => toggleProductExpanded('1')}
                  className="p-3 hover:bg-black/10 rounded-full bg-black/5 transition-colors"
                >
                  <img
                    src="/icons/up-arrow.svg"
                    alt="toggle"
                    className={`w-4 h-4 transition-transform ${
                      !expandedProducts.has('1') ? 'rotate-180' : ''
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {expandedProducts.has('1') && (
            <div className="p-6 space-y-3 pt-0 max-md:pb-4 max-md:px-3">
            <div className="rounded-xl p-5 border border-gray-200 max-md:p-3">
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900">Overview</h4>
              </div>
              <div className="flex flex-row max-md:gap-10 max-sm:gap-4 max-2xs:gap-3 max-xxl:flex-col">
                <div className="xxl:border-r-2 max-xxl:border-b w-full max-xxl:pb-4 max-xxl:mb-4 max-md:mb-0 max-md:pr-6 max-2xs:pr-2 xxl:w-48 gap-3 flex flex-col">
                  <span className="text-sm text-gray-600 block">Admin:</span>
                  <p className="font-medium text-gray-900 max-md:text-sm max-md:font-normal">
                    Processing Admin
                  </p>
                </div>
                <div className="xxl:ml-2 xxl:border-r-2 max-xxl:border-b w-full max-xxl:pb-4 max-xxl:mb-4 max-md:mb-0 xxl:w-40 gap-3 flex flex-col">
                  <span className="text-sm text-gray-600 block">ID:</span>
                  <p className="font-medium text-gray-900 max-md:text-sm max-md:font-normal">
                    1234564
                  </p>
                </div>
                <div className="xxl:ml-2 xxl:border-r-2 max-xxl:border-b w-full max-xxl:pb-4 max-xxl:mb-4 max-md:mb-0 xxl:w-40 gap-3 flex flex-col">
                  <span className="text-sm text-gray-600 block">Sub-category:</span>
                  <p className="font-medium text-gray-900 max-md:text-sm max-md:font-normal">
                    Admin
                  </p>
                </div>
                <div className="flex ml-2 max-xxl:ml-0 max-lg:pb-2 border-r-0 w-1/3 md:w-40 gap-3 flex-col">
                  <span className="text-sm text-gray-600 block">Status:</span>
                  <span className="px-3 py-1 rounded-full w-fit text-xs text-white status-done">
                    Active
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col xxl:flex-row gap-3">
              <div className="rounded-xl p-5 border border-gray-200 w-full xxl:w-1/2 max-md:p-3">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-sm font-semibold text-gray-900">Fees</h4>
                  <select className="text-sm border border-gray-300 rounded px-2 py-1">
                    <option>Monthly</option>
                    <option>Yearly</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Member (7839):</span>
                    <span><span className="font-medium">$12.68</span> / month</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Billing Adjustment:</span>
                    <span><span className="font-medium">$5.00</span> / month</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Billing Fee:</span>
                    <span><span className="font-medium">$5.00</span> / month</span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl p-5 border border-gray-200 w-full xxl:w-1/2 max-md:p-3">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Options</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Website Display</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-800"></div>
                    </label>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Website Order</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-800"></div>
                    </label>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Commissionable</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-800"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Commission Card - Full Width */}
            <div className="rounded-xl p-5 border border-gray-200 max-md:p-3">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-semibold text-gray-900">Commission</h4>
                <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors">
                  <img src="/icons/plus.svg" alt="add" className="w-4 h-4" />
                  Add
                </button>
              </div>
            </div>
          </div>
          )}
        </div>

        <div className="bg-white rounded-xl overflow-hidden">
          <div className="p-4 pt-6 pl-6 max-md:p-3">
            <div className="flex justify-between items-start max-lg:items-center">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  Processing Fee
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button className="hidden md:block p-3 hover:bg-black/10 rounded-full bg-black/5 transition-colors">
                  <img
                    src="/icons/members/edit.svg"
                    alt="edit"
                    className="w-4 h-4"
                  />
                </button>
                <button className="hidden md:block p-3 hover:bg-black/10 rounded-full bg-black/5 transition-colors">
                  <img
                    src="/icons/delete.svg"
                    alt="delete"
                    className="w-4 h-4"
                  />
                </button>
                <button 
                  onClick={() => toggleProductExpanded('2')}
                  className="p-3 hover:bg-black/10 rounded-full bg-black/5 transition-colors"
                >
                  <img
                    src="/icons/up-arrow.svg"
                    alt="toggle"
                    className={`w-4 h-4 transition-transform ${
                      !expandedProducts.has('2') ? 'rotate-180' : ''
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Add Product Button */}
      <div className="flex items-center w-full justify-center gap-3 mt-6 md:hidden">
        <button className="flex w-full justify-center items-center gap-2 px-4 py-2 bg-[#831843] text-white max-md:text-sm rounded-full hover:bg-[#9B1F4F] transition-colors">
          <img
            src="/icons/plus.svg"
            alt="add"
            className="w-4 h-4 brightness-0 invert"
          />
          Add Product
        </button>
      </div>
    </div>
  );

  const renderDetailView = () => (
    <>
      <div className="px-8 py-6 flex items-center justify-between max-md:p-2">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToList}
            className="p-2 hover:bg-gray-100 flex items-center rounded-full"
          >
            <img src="/icons/left.svg" alt="left" />
          </button>
          <h1 className="id-card-title font-medium max-xxl:!text-2xl max-md:!text-xl">
            {selectedGroup?.label || 'Group Details'}
          </h1>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 text-secondary">
          <img
            className="w-4.5"
            src="/icons/members/edit-primary.svg"
            alt="edit"
          />
          Edit
        </button>
      </div>

      <div className="px-8 py-6 bg-white/50 big-corner max-md:p-2 max-md:bg-transparent">
        <div className="bg-[#71717A]/10 card-corner p-1">
          <div className="flex overflow-x-auto scrollbar-hide max-md:flex-nowrap max-md:gap-0">
            <button 
              onClick={() => handleTabChange('information')}
              className={`px-4 py-2 max-xxl:px-3 max-xxl:py-1.5 w-1/2 max-md:px-3 max-md:py-1.5 card-corner font-medium transition-colors whitespace-nowrap text-sm flex-shrink-0 ${
                activeTab === 'information'
                  ? 'bg-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Information
            </button>
            <button 
              onClick={() => handleTabChange('products')}
              className={`px-4 py-2 max-xxl:px-3 max-xxl:py-1.5 card-corner w-1/2 font-medium transition-colors whitespace-nowrap text-sm ${
                activeTab === 'products'
                  ? 'bg-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Products
            </button>
          </div>
        </div>

        {activeTab === 'information' ? (
          <div className="mt-6">
            <div className="flex gap-6 max-xxl:flex-col">
              {/* Agent Group Card */}
              <div className="bg-white big-corner p-6 w-1/2 max-xxl:w-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg max-sm:text-sm">
                    Agent Group
                  </h3>
                  <button
                    onClick={() => setIsEditingAgentGroup(!isEditingAgentGroup)}
                    className="p-2.5 text-secondary hover:bg-black/10 rounded-full bg-black/5"
                  >
                    <img className="w-4.5" src="/icons/members/edit.svg" alt="edit" />
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Company:
                    </span>
                    {isEditingAgentGroup ? (
                      <input
                        type="text"
                        value={editData.company}
                        onChange={(e) => setEditData(prev => ({ ...prev, company: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <span className="max-xxl:font-medium">{editData.company}</span>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Representative:
                    </span>
                    {isEditingAgentGroup ? (
                      <input
                        type="text"
                        value={editData.representative}
                        onChange={(e) => setEditData(prev => ({ ...prev, representative: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <span className="max-xxl:font-medium">{editData.representative}</span>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Agent-Group ID:
                    </span>
                    {isEditingAgentGroup ? (
                      <input
                        type="text"
                        value={editData.agentGroupId}
                        onChange={(e) => setEditData(prev => ({ ...prev, agentGroupId: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <span className="max-xxl:font-medium">{editData.agentGroupId}</span>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Created on:
                    </span>
                    {isEditingAgentGroup ? (
                      <input
                        type="text"
                        value={editData.createdOn}
                        onChange={(e) => setEditData(prev => ({ ...prev, createdOn: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <span className="max-xxl:font-medium">{editData.createdOn} • 10:34 am</span>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Address:
                    </span>
                    {isEditingAgentGroup ? (
                      <input
                        type="text"
                        value={editData.address}
                        onChange={(e) => setEditData(prev => ({ ...prev, address: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <span className="max-xxl:font-medium">{editData.address}</span>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Local Time:
                    </span>
                    {isEditingAgentGroup ? (
                      <input
                        type="text"
                        value={editData.localTime}
                        onChange={(e) => setEditData(prev => ({ ...prev, localTime: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <span className="max-xxl:font-medium">{editData.localTime}</span>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Contacts:
                    </span>
                    <span className="font-medium">
                      {selectedGroup?.phone} • <a href="#" className="text-blue-600 underline">{selectedGroup?.email}</a>
                    </span>
                  </div>
                </div>
                {isEditingAgentGroup && (
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleSaveAgentGroup}
                      className="px-4 py-2 bg-[#831843] text-white rounded hover:bg-[#9B1F4F] transition-colors text-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelAgentGroup}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Setup Card */}
              <div className="bg-white big-corner p-6 w-1/2 max-xxl:w-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg max-sm:text-sm">
                    Setup
                  </h3>
                  <button
                    onClick={() => setIsEditingSetup(!isEditingSetup)}
                    className="p-2.5 text-secondary hover:bg-black/10 rounded-full bg-black/5"
                  >
                    <img className="w-4.5" src="/icons/members/edit.svg" alt="edit" />
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Parent:
                    </span>
                    {isEditingSetup ? (
                      <input
                        type="text"
                        value={editData.parent}
                        onChange={(e) => setEditData(prev => ({ ...prev, parent: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <a href="#" className="text-blue-600 underline font-medium">{editData.parent}</a>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Website:
                    </span>
                    {isEditingSetup ? (
                      <input
                        type="text"
                        value={editData.website}
                        onChange={(e) => setEditData(prev => ({ ...prev, website: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <a href="#" className="text-blue-600 underline font-medium">{editData.website}</a>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Management:
                    </span>
                    {isEditingSetup ? (
                      <input
                        type="text"
                        value={editData.management}
                        onChange={(e) => setEditData(prev => ({ ...prev, management: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <a href="#" className="text-blue-600 underline font-medium">{editData.management}</a>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Domain Name:
                    </span>
                    {isEditingSetup ? (
                      <input
                        type="text"
                        value={editData.domainName}
                        onChange={(e) => setEditData(prev => ({ ...prev, domainName: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <span className="max-xxl:font-medium">{editData.domainName}</span>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Secure Domain Name:
                    </span>
                    {isEditingSetup ? (
                      <input
                        type="text"
                        value={editData.secureDomainName}
                        onChange={(e) => setEditData(prev => ({ ...prev, secureDomainName: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <span className="max-xxl:font-medium">{editData.secureDomainName}</span>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1 max-lg:border-b max-lg:pb-2">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Status:
                    </span>
                    {isEditingSetup ? (
                      <select
                        value={editData.status}
                        onChange={(e) => setEditData(prev => ({ ...prev, status: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    ) : (
                      <span className="font-medium text-green-600">{editData.status}</span>
                    )}
                  </div>
                  <div className="flex text-sm max-xxl:flex-col max-xxl:gap-1">
                    <span className="text-gray-500 w-[40%] max-xxl:w-full">
                      Tax ID:
                    </span>
                    {isEditingSetup ? (
                      <input
                        type="text"
                        value={editData.taxId}
                        onChange={(e) => setEditData(prev => ({ ...prev, taxId: e.target.value }))}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <span className="font-medium">{editData.taxId}</span>
                    )}
                  </div>
                </div>
                {isEditingSetup && (
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={handleSaveSetup}
                      className="px-4 py-2 bg-[#831843] text-white rounded hover:bg-[#9B1F4F] transition-colors text-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelSetup}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Group Card - Full Width */}
            <div className="bg-white big-corner p-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg max-sm:text-sm">
                  Group
                </h3>
                <button
                  onClick={() => setIsEditingGroup(!isEditingGroup)}
                  className="p-2.5 text-secondary hover:bg-black/10 rounded-full bg-black/5"
                >
                  <img className="w-4.5" src="/icons/members/edit.svg" alt="edit" />
                </button>
              </div>
              <div className="flex gap-4 max-lg:flex-col">
                <div className="border-r-2 max-lg:border-r-0 pr-4 flex flex-col max-lg:border-b max-lg:pb-2">
                  <span className="text-sm text-gray-500">List Bill Invoice:</span>
                  {isEditingGroup ? (
                    <input
                      type="text"
                      value={editData.listBillInvoice}
                      onChange={(e) => setEditData(prev => ({ ...prev, listBillInvoice: e.target.value }))}
                      className="px-2 py-1 border border-gray-300 rounded text-sm mt-1"
                    />
                  ) : (
                    <span className="font-medium">
                      Yes - Online ACH - <a href="#" className="text-blue-600 underline">Summary</a>
                    </span>
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-500">Invoice Template:</span>
                  {isEditingGroup ? (
                    <input
                      type="text"
                      value={editData.invoiceTemplate}
                      onChange={(e) => setEditData(prev => ({ ...prev, invoiceTemplate: e.target.value }))}
                      className="px-2 py-1 border border-gray-300 rounded text-sm mt-1"
                    />
                  ) : (
                    <span className="font-medium">{editData.invoiceTemplate}</span>
                  )}
                </div>
              </div>
              {isEditingGroup && (
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handleSaveGroup}
                    className="px-4 py-2 bg-[#831843] text-white rounded hover:bg-[#9B1F4F] transition-colors text-sm"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelGroup}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          renderProductsTab()
        )}
      </div>
    </>
  );

  return (
    <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-xxl:my-2.5 relative">
      {viewMode === 'list' ? (
        <>
      <div className="px-8 py-6 flex items-center justify-between max-xxl:flex-row max-xxl:items-baseline max-xxl:flex-col max-xxl:px-2 max-container">
        <h1 className="id-card-title font-medium max-xxl:!text-2xl max-md:!text-xl">
          Groups
        </h1>
        <div className="hidden lg:flex items-center justify-end gap-3.5 text-secondary max-xxl:flex-wrap max-xxl:mt-4 w-full">
          {navigationItems.map((item, index) => (
            <button
              key={item.label}
              className={`flex gap-2 items-center ${
                index < navigationItems.length - 1
                  ? 'border-r-2 border-black/10 pr-3.5'
                  : ''
              }`}
            >
              <img
                className="w-5 h-5"
                src={`/icons/members/${item.icon}`}
                alt={item.label.toLowerCase()}
              />
              <p>{item.label}</p>
            </button>
          ))}
        </div>
        <button
          onClick={handleToggleMobileMenu}
          className="flex gap-2 items-center p-2 border rounded-full border-gray-300 lg:hidden"
        >
          <img className="w-4 h-4" src="/icons/dots.svg" alt="menu" />
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-20 left-4 right-4 z-50">
          <div className="bg-white rounded-[20px] border border-gray-200 p-4 shadow-lg">
            <div className="grid grid-cols-2 gap-3">
              {navigationItems.map(item => (
                <button
                  key={item.label}
                  className="flex gap-2 items-center p-2 border rounded-full border-gray-300"
                  onClick={handleCloseMobileMenu}
                >
                  <img
                    className="w-5 h-5"
                    src={`/icons/members/${item.icon}`}
                    alt={item.label.toLowerCase()}
                  />
                  <p className="text-sm text-secondary">{item.label}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="px-2 pb-6 max-md:px-2 max-container">
        <div className="bg-white-overlay big-corner p-6 max-md:p-0 max-md:bg-transparent">
          <div className="flex justify-between max-md:flex-col items-center mb-6 max-xxl:flex-col-reverse max-xxl:items-baseline max-xxl:gap-4">
            <div className="flex gap-4 w-[40%] max-xxl:w-[60%] max-md:w-full">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search by name or member ID..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-8 h-10 border rounded-lg focus:outline-none focus:ring-2 id-card-input"
                />
                <img
                  src="/icons/Search.svg"
                  alt="search"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                />
              </div>
              <button
                onClick={() => setSearchTerm('')}
                className="px-6 py-2 max-md:hidden rounded-full bg-secondary-bg text-white hover:bg-opacity-90 transition-colors"
              >
                Search
              </button>
            </div>
            <div className="flex items-center gap-2 max-md:w-full">
              <span className="text-sm font-medium">Sort</span>
              <select
                value={`${sortField}-${sortDirection}`}
                onChange={e => {
                  const [field, direction] = e.target.value.split('-') as [
                    SortField,
                    SortDirection
                  ];
                  setSortField(field);
                  setSortDirection(direction);
                }}
                className="px-4 py-2 border rounded-lg w-48 max-md:w-full focus:outline-none focus:ring-2 id-card-input"
              >
                <option value="created-desc">Newest to Oldest</option>
                <option value="created-asc">Oldest to Newest</option>
                <option value="label-asc">Name A-Z</option>
                <option value="label-desc">Name Z-A</option>
              </select>
            </div>
          </div>

          <div className="hidden 1xl:block overflow-x-auto 1xl:w-[95%] 2xl:w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center gap-2">
                      ID {getSortIcon('id')}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('label')}
                  >
                    <div className="flex items-center gap-2">
                      Label {getSortIcon('label')}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('representative')}
                  >
                    <div className="flex items-center gap-2">
                      Representative {getSortIcon('representative')}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('city')}
                  >
                    <div className="flex items-center gap-2">
                      City {getSortIcon('city')}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center gap-2">
                      Contacts {getSortIcon('email')}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('policyHolders')}
                  >
                    <div className="flex items-center gap-2">
                      Policy Holders {getSortIcon('policyHolders')}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('created')}
                  >
                    <div className="flex items-center gap-2">
                      Created {getSortIcon('created')}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('agent')}
                  >
                    <div className="flex items-center gap-2">
                      Agent {getSortIcon('agent')}
                    </div>
                  </TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentData.map((group, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">
                      {group.id}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <a 
                          href="#" 
                          className="hover:opacity-80 transition-opacity"
                        >
                          <img src="/icons/brows.svg" alt="" />
                        </a>
                        {group.label}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{group.representative}</TableCell>
                    <TableCell>{group.city}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{group.email}</div>
                        <div>{group.phone}</div>
                      </div>
                    </TableCell>
                    <TableCell>{group.policyHolders}</TableCell>
                    <TableCell>{group.created}</TableCell>
                    <TableCell>
                      <a href="#" className="text-blue-600 underline font-medium">
                        {group.agent}
                      </a>
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={e => handleContextMenuClick(e, group.id)}
                        className="px-2 py-3.5 border-2 border-gray-200 hover:bg-gray-100 rounded-full"
                      >
                        <img
                          className="min-w-[10px]"
                          src="/icons/dots.svg"
                          alt="dots"
                        />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="1xl:hidden space-y-4">
            {currentData.map((group, index) => (
              <div
                key={index}
                className="bg-white rounded-[20px] border border-gray-200 p-4 shadow-sm"
              >
                <div className="flex justify-between items-start mb-4 max-lg:items-center">
                  <div>
                    <p className="font-bold text-lg mb-1">{group.id}</p>
                    <div className="flex items-center gap-2 max-md:gap-1">
                      <h3 className="font-semibold max-1xl:text-[#71717A] max-md:font-normal text-lg max-2xs:text-sm">
                        {group.representative}
                      </h3>
                      <span className="text-gray-400">•</span>
                      <a
                        href="#"
                        className="text-blue-600 underline text-sm"
                      >
                        {group.agent}
                      </a>
                    </div>
                  </div>
                  <button
                    onClick={e => handleContextMenuClick(e, group.id)}
                    className="p-2 border border-gray-300 hover:bg-gray-100 rounded-full"
                  >
                    <img
                      className="w-4 h-4"
                      src="/icons/dots.svg"
                      alt="dots"
                    />
                  </button>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">City:</span>
                    <span>{group.city}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date Created:</span>
                    <span>{group.created}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="text-blue-600">{group.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone Number:</span>
                    <span>{group.phone}</span>
                  </div>
                </div>

                <div className="rounded-xl p-2.5 border border-[#E4E4E7]">
                  <div className="flex justify-between items-center">
                    <a href="#" className="hover:opacity-80 transition-opacity">
                      <span className="text-sm">{group.label}</span>
                    </a>
                    <a 
                      href="#" 
                      className="text-blue-600 underline text-sm hover:opacity-80 transition-opacity flex items-center gap-1"
                    >
                      <img src="/icons/brows.svg" alt="" className="w-4 h-4" />
                      Website
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-6">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() =>
                      handlePageChange(Math.max(1, currentPage - 1))
                    }
                    className={
                      currentPage === 1
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>

                {Array.from(
                  { length: Math.min(totalPages, 20) },
                  (_, i) => i + 1
                ).map(page => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => handlePageChange(page)}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                {totalPages > 20 && (
                  <>
                    <PaginationItem>
                      <span className="px-3 py-2">...</span>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => handlePageChange(totalPages)}
                        className="cursor-pointer"
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  </>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      handlePageChange(
                        Math.min(totalPages, currentPage + 1)
                      )
                    }
                    className={
                      currentPage === totalPages
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </div>

      {contextMenu.isOpen && (
        <div
          className="fixed bg-white card-corner shadow-lg border border-gray-200 z-50 w-[132px] flex flex-col"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
        >
          <button
            onClick={() => handleWebsiteGroup(contextMenu.groupId)}
            className="block w-full px-3 py-3 text-left text-sm text-gray-700 font-medium hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            Website
          </button>
          <button
            onClick={() => handleViewGroup(contextMenu.groupId)}
            className="block w-full px-3 py-3 text-left text-sm text-gray-700 font-medium hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            View
          </button>
          <button
            onClick={() => handleDeleteGroup(contextMenu.groupId)}
            className="block w-full px-3 py-3 text-left text-sm text-red-600 font-medium hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            Delete
          </button>
        </div>
      )}

      {contextMenu.isOpen && (
        <div className="fixed inset-0 z-40" onClick={handleCloseContextMenu} />
      )}
        </>
      ) : (
        renderDetailView()
      )}
    </div>
  );
};

export default RegularGroups;
